import React, { useState } from "react";
import { cn } from "../../../lib/utils";

const SECTIONS = [
  {
    id: "complete-profile",
    title: "Complete Your Profile",
    content: (
      <>
        <p className="mb-4">
          Before MarkBase can analyze or execute, it needs context and access to your systems.
        </p>
        <h3 className="font-semibold mb-2">Step 1: Complete Your Profile</h3>
        <p className="mb-2">Go to: <strong>Account → Profile</strong></p>
        <p className="mb-4">
          These fields define how MarkBase interprets your business and generates strategic recommendations.
        </p>
        <h4 className="font-medium mb-1">Industry & Business Type</h4>
        <p className="mb-4">
          Specify whether you operate B2B or B2C. Marketing logic differs significantly across business models and industries.
        </p>
        <h4 className="font-medium mb-1">Target Audience Details</h4>
        <p className="mb-2">This is the most important field. Clearly describe:</p>
        <ul className="list-disc list-inside mb-2 space-y-1">
          <li>Job titles</li>
          <li>Decision-makers</li>
          <li>Pain points</li>
          <li>Buyer type</li>
        </ul>
        <p className="mb-2">Example:</p>
        <p className="mb-4 italic text-muted-foreground">
          &ldquo;CMOs at SaaS companies with 50–200 employees looking to reduce CAC.&rdquo;
        </p>
        <h4 className="font-medium mb-1">Employee Size & Revenue Range</h4>
        <p className="mb-6">
          Helps MarkBase identify ideal company profiles during contact discovery and strategic targeting.
        </p>

        <h3 className="font-semibold mb-2">Step 2: Connect Your Marketing Ecosystem</h3>
        <p className="mb-2">Go to: <strong>Channels</strong></p>
        <p className="mb-4">Connect your platforms to allow MarkBase to analyze performance and execute campaigns.</p>
        <h4 className="font-medium mb-2">Ad Channels</h4>
        <ul className="list-disc list-inside mb-4 space-y-1">
          <li>Google Ads</li>
          <li>LinkedIn Ads</li>
          <li>Meta (Facebook & Instagram)</li>
        </ul>
        <h4 className="font-medium mb-2">Analytics</h4>
        <ul className="list-disc list-inside mb-4 space-y-1">
          <li>Google Analytics 4 (GA4)</li>
        </ul>
        <p className="mb-2">Once connected, MarkBase can:</p>
        <ul className="list-disc list-inside mb-6 space-y-1">
          <li>Analyze cross-channel performance</li>
          <li>Identify inefficiencies</li>
          <li>Trigger outreach campaigns</li>
          <li>Coordinate strategy across platforms</li>
        </ul>

        <h3 className="font-semibold mb-2">Step 3: Connect Your Email Domain (Required for Sending Emails)</h3>
        <p className="mb-4">
          Before MarkBase can send outbound emails on your behalf, you must verify your sending domain.
        </p>
        <p className="mb-4">Go to: <strong>Channels → Email Setup</strong></p>
        <h4 className="font-medium mb-2">1. Enter Your Sending Email</h4>
        <p className="mb-2">Enter the email address you want to send from.</p>
        <p className="mb-2">Example: <code className="bg-muted px-1 rounded">hello@yourcompany.com</code></p>
        <p className="mb-4">This should be a domain you control.</p>
        <h4 className="font-medium mb-2">2. Add DNS Records</h4>
        <p className="mb-2">After entering your email, MarkBase will generate three CNAME records. You must:</p>
        <ul className="list-disc list-inside mb-2 space-y-1">
          <li>Log in to your domain provider (e.g., GoDaddy, Cloudflare, Namecheap).</li>
          <li>Go to your DNS settings.</li>
          <li>Create the three CNAME records exactly as shown in MarkBase.</li>
          <li>Save the changes.</li>
        </ul>
        <p className="mb-4 text-sm">
          <strong>Important:</strong> Do not modify the values. DNS changes may take a few minutes to propagate.
        </p>
        <h4 className="font-medium mb-2">3. Verify Domain</h4>
        <p className="mb-2">Once the DNS records are added:</p>
        <ul className="list-disc list-inside mb-2 space-y-1">
          <li>Return to MarkBase.</li>
          <li>Click Verify.</li>
          <li>If the setup is correct, you will see a green check mark indicating your domain is verified.</li>
        </ul>
        <p>The more precise your profile, the more accurate your insights and outreach execution will be. You are now ready to send emails.</p>
      </>
    ),
  },
  {
    id: "insights-reporting",
    title: "Insights and Reporting",
    content: (
      <>
        <p className="mb-4">
          MarkBase unifies your connected platforms into a single intelligence layer. Instead of exporting files or building manual dashboards, you ask direct questions in plain English and receive structured answers with context.
        </p>
        <p className="mb-4">When your channels are connected, MarkBase can analyze:</p>
        <ul className="list-disc list-inside mb-4 space-y-1">
          <li>Google Ads performance</li>
          <li>Meta (Facebook and Instagram) campaigns</li>
          <li>LinkedIn Ads performance</li>
          <li>Google Analytics 4 website data</li>
          <li>Cross-channel ROI and funnel efficiency</li>
        </ul>
        <p className="mb-6">
          It does not just return metrics. It interprets trends, highlights inefficiencies, and identifies opportunities for improvement.
        </p>

        <h3 className="font-semibold mb-2">Google Analytics 4 (Website Performance)</h3>
        <p className="mb-4">
          Use MarkBase to understand how people find your website, what they do once they arrive, and which pages drive results.
        </p>
        <p className="mb-2 font-medium">Example questions:</p>
        <ul className="list-disc list-inside mb-4 space-y-1 text-muted-foreground">
          <li>How many users visited our website this month compared to last month?</li>
          <li>How many new users did we get in the last 30 days?</li>
          <li>What are our top traffic sources this month?</li>
          <li>How much traffic are we getting from organic search? Paid search? Google Ads specifically?</li>
          <li>Which social platforms are sending the most traffic to our site?</li>
          <li>Which states or cities are driving the most traffic?</li>
          <li>What are our most visited pages in the last 30 days? Top 5 by views?</li>
          <li>What is our bounce rate? Is our engagement rate improving?</li>
          <li>What is the average session duration? Mobile vs desktop?</li>
          <li>Which landing pages generate the most leads? Which traffic source drives highest conversions or revenue?</li>
          <li>How many users clicked our contact us button? Completed checkout? What is the conversion rate for our contact page or a specific page URL?</li>
        </ul>
        <p className="mb-6">
          MarkBase can break answers down by date range, page/landing page, source/medium, region/state/city/country, device category, and events/conversions (including custom).
        </p>

        <h3 className="font-semibold mb-2">Google Ads</h3>
        <p className="mb-4">Use MarkBase to evaluate spend efficiency, keyword performance, and campaign results.</p>
        <p className="mb-2 font-medium">Example questions:</p>
        <ul className="list-disc list-inside mb-4 space-y-1 text-muted-foreground">
          <li>Which campaigns have the highest return on ad spend right now?</li>
          <li>What keywords are spending money but generating no conversions?</li>
          <li>How has our cost per click changed compared to last month?</li>
          <li>Which time of day generates the lowest cost per acquisition?</li>
          <li>Should we increase the budget on any campaign based on ROI?</li>
        </ul>
        <p className="mb-2">MarkBase helps identify:</p>
        <ul className="list-disc list-inside mb-6 space-y-1">
          <li>Budget inefficiencies</li>
          <li>Conversion gaps</li>
          <li>High-performing keywords that are underfunded</li>
          <li>Campaigns that should be paused or scaled</li>
        </ul>

        <h3 className="font-semibold mb-2">Meta Ads (Facebook and Instagram)</h3>
        <p className="mb-4">Analyze creative performance, audience targeting, and conversion trends.</p>
        <p className="mb-2 font-medium">Example questions:</p>
        <ul className="list-disc list-inside mb-4 space-y-1 text-muted-foreground">
          <li>Which creative format is performing better, video or static?</li>
          <li>Are we experiencing ad fatigue?</li>
          <li>Which audience has the lowest cost per lead?</li>
          <li>Is our retargeting campaign driving incremental sales?</li>
          <li>How does Instagram performance compare to Facebook?</li>
        </ul>
        <p className="mb-2">MarkBase highlights:</p>
        <ul className="list-disc list-inside mb-6 space-y-1">
          <li>Creative fatigue signals</li>
          <li>Audience performance breakdown</li>
          <li>Spend versus revenue alignment</li>
          <li>Funnel inconsistencies</li>
        </ul>

        <h3 className="font-semibold mb-2">LinkedIn Ads</h3>
        <p className="mb-4">Understand B2B targeting, job-level engagement, and campaign effectiveness.</p>
        <p className="mb-2 font-medium">Example questions:</p>
        <ul className="list-disc list-inside mb-4 space-y-1 text-muted-foreground">
          <li>Which job titles convert best?</li>
          <li>What is our cost per Director-level lead?</li>
          <li>Are Sponsored Content ads outperforming Lead Gen Forms?</li>
          <li>Which company size responds best to our messaging?</li>
          <li>How does LinkedIn cost per click compare to Google Ads?</li>
        </ul>
        <p className="mb-2">MarkBase identifies:</p>
        <ul className="list-disc list-inside mb-6 space-y-1">
          <li>Targeting mismatches</li>
          <li>Seniority-level performance trends</li>
          <li>Industry-specific conversion differences</li>
          <li>Cross-channel cost comparisons</li>
        </ul>

        <h3 className="font-semibold mb-2">Cross-Channel Strategy</h3>
        <p className="mb-4">MarkBase connects all platforms to provide a unified view of marketing performance.</p>
        <p className="mb-2 font-medium">Example strategic questions:</p>
        <ul className="list-disc list-inside mb-4 space-y-1 text-muted-foreground">
          <li>What is our total blended return on ad spend across all channels?</li>
          <li>Where are we spending money but seeing no website engagement?</li>
          <li>Which channel should we scale and which should we reduce?</li>
          <li>Where is the largest drop-off in our funnel?</li>
          <li>How can we reduce customer acquisition cost by 20 percent?</li>
        </ul>
        <p className="mb-4">You can also request year-to-date spend versus revenue summary, geographic performance comparisons, funnel stage analysis, and channel efficiency breakdown.</p>

        <h3 className="font-semibold mb-2">How to Use Reporting Effectively</h3>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>Start simple</strong> — Ask high-level performance questions first.</li>
          <li><strong>Drill deeper</strong> — Follow up with more specific questions based on the response.</li>
          <li><strong>Take action</strong> — Use the insights to adjust budgets, audiences, and messaging.</li>
        </ul>
        <p className="mt-4">MarkBase is designed to help you move from insight to execution.</p>
      </>
    ),
  },
  {
    id: "search-contacts",
    title: "Contact Discovery and Company Intelligence",
    content: (
      <>
        <p className="mb-4">
          MarkBase allows you to discover decision-makers and company information directly through the chat interface. Instead of navigating databases or applying manual filters, you can simply describe the type of contacts or companies you are looking for.
        </p>
        <p className="mb-4">
          MarkBase searches its data sources and returns structured information about individuals and organizations, allowing you to quickly identify potential leads, partners, or prospects.
        </p>

        <h3 className="font-semibold mb-2">You can search by attributes such as:</h3>
        <ul className="list-disc list-inside mb-6 space-y-1">
          <li>Job title</li>
          <li>Department</li>
          <li>Seniority level</li>
          <li>Industry</li>
          <li>Company revenue range</li>
          <li>Employee size</li>
          <li>Company location</li>
          <li>Keywords related to the business</li>
        </ul>

        <h3 className="font-semibold mb-2">Example prompts</h3>
        <ul className="list-disc list-inside mb-6 space-y-1 text-muted-foreground">
          <li>Find CEOs of software companies with $5M to $25M in revenue.</li>
          <li>Find marketing directors at manufacturing companies in California.</li>
          <li>Find founders of fintech startups with 50 to 200 employees.</li>
        </ul>

        <h3 className="font-semibold mb-2">Contact information provided</h3>
        <p className="mb-2">For each result, MarkBase may return available contact information such as:</p>
        <ul className="list-disc list-inside mb-6 space-y-1">
          <li>First Name</li>
          <li>Last Name</li>
          <li>Title</li>
          <li>Department</li>
          <li>Seniority</li>
          <li>LinkedIn Profile</li>
          <li>Work Email</li>
          <li>Personal Email</li>
          <li>Contact Phone</li>
        </ul>

        <h3 className="font-semibold mb-2">Company information provided</h3>
        <p className="mb-2">MarkBase also returns relevant company-level details when available, including:</p>
        <ul className="list-disc list-inside mb-6 space-y-1">
          <li>Company Name</li>
          <li>Website</li>
          <li>Industry</li>
          <li>Company Description</li>
          <li>Founded Date</li>
          <li>Annual Revenue</li>
          <li>Revenue Range</li>
          <li>Staff Count</li>
          <li>Company Location</li>
          <li>Company LinkedIn Profile</li>
        </ul>

        <h3 className="font-semibold mb-2">Research specific individuals or companies</h3>
        <p className="mb-2">You can also ask MarkBase to research a specific person or company.</p>
        <p className="mb-2 font-medium">Example prompts:</p>
        <ul className="list-disc list-inside mb-4 space-y-1 text-muted-foreground">
          <li>Give me information about [Full Name].</li>
          <li>Show me company details for [Company Name].</li>
          <li>What is the estimated revenue range of [Company Name]?</li>
          <li>Who are the executives at [Company Name]?</li>
        </ul>
        <p className="mb-6">MarkBase will return structured contact and company information when it is available in the system.</p>

        <h3 className="font-semibold mb-2">Using contacts for outreach</h3>
        <p className="mb-4">Once you identify relevant contacts, you can immediately use MarkBase to send outreach emails through your connected email domain.</p>
        <p className="mb-2 font-medium">Example commands:</p>
        <ul className="list-disc list-inside mb-4 space-y-1 text-muted-foreground">
          <li>Email 30 CEOs of software companies in the $5M to $25M range and invite them to our webinar.</li>
          <li>Send a personalized invitation to these founders for our upcoming event.</li>
          <li>Invite software executives to our Snowflake AI Summit session.</li>
        </ul>
        <p>MarkBase will draft the messages, personalize them based on your business profile, and send them through your verified email domain.</p>
      </>
    ),
  },
  {
    id: "markmail",
    title: "MarkMail – Email Execution",
    content: (
      <>
        <p className="mb-4">
          MarkMail allows you to send outbound emails directly from MarkBase using your verified domain.
        </p>
        <p className="mb-6">Before sending emails, ensure your email domain is verified (see Section 1), your profile information is complete, and your contact list is ready. MarkMail supports three primary workflows.</p>

        <h3 className="font-semibold mb-2">Option 1: Natural Language Command</h3>
        <p className="mb-4">You can instruct MarkBase directly in the command bar.</p>
        <p className="mb-2 font-medium">Examples:</p>
        <ul className="list-disc list-inside mb-4 space-y-1 text-muted-foreground">
          <li>Find 25 CEOs of software companies with $10M to $50M in revenue and invite them to our Snowflake AI Summit event.</li>
          <li>Email 40 marketing directors in healthcare and invite them to our March webinar.</li>
          <li>Send a follow-up email to everyone who attended our last demo.</li>
        </ul>
        <p className="mb-6">MarkBase will identify matching contacts, draft personalized emails using your business profile, and prepare the campaign for sending. You can review and approve before sending.</p>

        <h3 className="font-semibold mb-2">Option 2: CSV Upload</h3>
        <p className="mb-4">If you already have a list, go to: <strong>Outreach → Upload CSV</strong></p>
        <p className="mb-2">Steps:</p>
        <ul className="list-disc list-inside mb-4 space-y-1">
          <li>Upload your file</li>
          <li>Map required fields (at minimum, Email)</li>
          <li>Confirm contact data</li>
          <li>Provide your instructions</li>
        </ul>
        <p className="mb-2 font-medium">Examples:</p>
        <ul className="list-disc list-inside mb-6 space-y-1 text-muted-foreground">
          <li>Write a follow-up sequence inviting these contacts to our demo day.</li>
          <li>Send an introductory email introducing our product.</li>
          <li>Invite this list to our upcoming event.</li>
        </ul>
        <p className="mb-6">MarkBase will generate the message and prepare delivery through your connected domain.</p>

        <h3 className="font-semibold mb-2">Option 3: Targeted Direct Email</h3>
        <p className="mb-4">To contact a specific individual, use a direct command.</p>
        <p className="mb-2 font-medium">Examples:</p>
        <ul className="list-disc list-inside mb-6 space-y-1 text-muted-foreground">
          <li>Email John Smith and invite him to our Demo Day this Friday.</li>
          <li>Send a partnership proposal to the CEO of [Company Name].</li>
        </ul>
        <p>If the contact exists in the database, MarkBase retrieves the available contact data and prepares the message for sending.</p>

        <h3 className="font-semibold mb-2 mt-6">Personalization and Context</h3>
        <p className="mb-4">MarkMail uses your company profile, industry information, target audience data, and contact firmographics so outreach is aligned with your positioning.</p>

        <h3 className="font-semibold mb-2">Best Practices</h3>
        <ul className="list-disc list-inside space-y-1">
          <li>Start with small batches to test deliverability</li>
          <li>Personalize subject lines when appropriate</li>
          <li>Monitor replies inside your connected inbox</li>
          <li>Maintain a clean sending domain</li>
        </ul>
        <p className="mt-4">MarkMail is designed to reduce manual outreach work while maintaining professional standards.</p>
      </>
    ),
  },
];

export default function UserGuidePage() {
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);

  return (
    <div className="flex gap-8 pt-4 -mx-4 px-4 h-[calc(100vh-8rem)] overflow-hidden">
      <aside className="w-48 shrink-0 border-r border-border pr-4">
        <nav className="space-y-1">
          {SECTIONS.map(({ id, title }) => (
            <button
              key={id}
              onClick={() => setActiveSection(id)}
              className={cn(
                "block w-full text-left py-2 px-3 rounded-lg text-sm font-medium transition-colors",
                activeSection === id
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
              )}
            >
              {title}
            </button>
          ))}
        </nav>
      </aside>
      <main className="flex-1 min-w-0 overflow-y-auto pb-8">
        {SECTIONS.map(({ id, title, content }) => (
          <section
            key={id}
            className={activeSection === id ? "block" : "hidden"}
          >
            <h2 className="text-xl font-bold mb-6">{title}</h2>
            <div className="space-y-4 text-sm leading-relaxed">
              {content}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
